// PROMPT 134 (16-oct-2026): avisarles a las agencias el cierre de una salida.
// Operaciones (Luisa): "desde el aplicativo se podia enviar el mensaje de cierres a todas las
// agencias, ya estaban seleccionados todos los correos". Arma el mensaje que usa hoy el equipo (con
// el tour y la fecha de esta salida), con las agencias que tienen correo de aviso ya tildadas. Se
// puede cambiar el texto y destildar agencias. Antes de mandar pide confirmacion: les llega de verdad.
// Sale un correo por direccion desde la casilla central (Interviajes NY); las respuestas caen en la
// casilla de siempre. Los correos de cada agencia se cargan en Configuracion -> Agencias.
import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { formatFechaLarga } from "@/lib/agenda";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Send, AlertTriangle, CheckCircle2, Mail } from "lucide-react";

export function cuandoSeMando(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("es-ES", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

export default function AvisoCierreDialog({ open, onOpenChange, salida, onSent }) {
  const [data, setData] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [asunto, setAsunto] = useState("");
  const [texto, setTexto] = useState("");
  const [elegidas, setElegidas] = useState([]);
  const [paso, setPaso] = useState("armar"); // armar | confirmar | enviando | listo
  const [resultado, setResultado] = useState(null);

  useEffect(() => {
    if (!open || !salida?.id) return;
    setData(null);
    setResultado(null);
    setPaso("armar");
    setCargando(true);
    api
      .get(`/salidas/${salida.id}/aviso-cierre`)
      .then(({ data: d }) => {
        setData(d);
        setAsunto(d.asunto || "");
        setTexto(d.texto || "");
        setElegidas((d.agencias || []).filter((a) => a.seleccionada).map((a) => a.id));
      })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setCargando(false));
  }, [open, salida?.id]);

  const agencias = useMemo(() => data?.agencias || [], [data]);
  const correos = useMemo(() => {
    const vistos = new Set();
    for (const a of agencias) if (elegidas.includes(a.id)) for (const c of a.correos) vistos.add(c);
    return vistos.size;
  }, [agencias, elegidas]);
  const listo = !!data?.desde?.listo;
  const maximo = data?.limites?.correos || 40;
  const puedeMandar =
    listo && elegidas.length > 0 && asunto.trim() && texto.trim() && correos <= maximo && paso === "armar";

  const alternar = (id, v) =>
    setElegidas((prev) => (v ? [...prev.filter((x) => x !== id), id] : prev.filter((x) => x !== id)));

  const mandar = async () => {
    setPaso("enviando");
    try {
      const ids = agencias.filter((a) => elegidas.includes(a.id)).map((a) => a.id);
      const { data: r } = await api.post(`/salidas/${salida.id}/aviso-cierre`, {
        agencia_ids: ids,
        asunto,
        texto,
      });
      setResultado(r);
      setPaso("listo");
      if (r.fallidos?.length) toast.warning(`Salieron ${r.enviados} de ${r.total} correos. Revisá los que no salieron.`);
      else toast.success(`Aviso mandado: ${r.enviados} correo(s) a ${r.agencias.length} agencia(s).`);
      onSent?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setPaso("armar");
    }
  };

  const ocupado = paso === "enviando";

  return (
    <Dialog open={open} onOpenChange={(v) => !ocupado && onOpenChange(v)}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="aviso-cierre-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Mail className="h-5 w-5" /> Avisar el cierre a las agencias
          </DialogTitle>
          <DialogDescription>
            {salida?.tour_nombre} · <span className="capitalize">{salida?.fecha ? formatFechaLarga(salida.fecha) : ""}</span>
            {salida?.hora ? ` · ${salida.hora}` : ""}
          </DialogDescription>
        </DialogHeader>

        {cargando || !data ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
          </div>
        ) : paso === "listo" && resultado ? (
          <div className="space-y-3" data-testid="aviso-cierre-resultado">
            <p className="flex items-center gap-2 text-sm text-slate-800">
              <CheckCircle2 className="h-5 w-5 text-emerald-600" />
              Salieron {resultado.enviados} de {resultado.total} correo(s), a {resultado.agencias.length} agencia(s).
            </p>
            {resultado.fallidos?.length > 0 && (
              <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-xs text-rose-700" data-testid="aviso-cierre-fallidos">
                <p className="font-medium mb-1">No salieron:</p>
                {resultado.fallidos.map((f) => (
                  <p key={f.correo}>
                    {f.correo} · {f.motivo}
                  </p>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {data.ultimo && (
              <p
                className="flex items-start gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800"
                data-testid="aviso-cierre-ultimo"
              >
                <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                Ya se mandó el {cuandoSeMando(data.ultimo.enviado_en)}
                {data.ultimo.por ? ` (lo mandó ${data.ultimo.por})` : ""} a {data.ultimo.agencias?.length || 0} agencia(s).
                Si lo mandás de nuevo, les llega otra vez.
              </p>
            )}
            {!listo && (
              <p
                className="flex items-start gap-1.5 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700"
                data-testid="aviso-cierre-sin-envio"
              >
                <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                El envío de correos de {data.desde?.nombre} no está configurado, así que el aviso no se puede mandar desde
                acá (Configuración → Marcas / Envío).
              </p>
            )}
            <p className="text-[11px] text-slate-500" data-testid="aviso-cierre-desde">
              Sale desde {data.desde?.nombre}
              {data.desde?.direccion ? ` (${data.desde.direccion})` : ""}, un correo por dirección.
              {data.desde?.responde_a ? ` Si una agencia contesta, la respuesta llega a ${data.desde.responde_a}.` : ""}
            </p>

            <div className="space-y-1.5">
              <Label className="text-xs">Asunto</Label>
              <Input
                value={asunto}
                onChange={(e) => setAsunto(e.target.value)}
                maxLength={data.limites?.asunto || 200}
                disabled={paso !== "armar"}
                data-testid="aviso-cierre-asunto"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Mensaje</Label>
              <Textarea
                value={texto}
                onChange={(e) => setTexto(e.target.value)}
                rows={8}
                maxLength={data.limites?.texto || 4000}
                disabled={paso !== "armar"}
                data-testid="aviso-cierre-texto"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs">
                  Agencias ({elegidas.length} de {agencias.length})
                </Label>
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs"
                    disabled={paso !== "armar"}
                    onClick={() => setElegidas(agencias.map((a) => a.id))}
                    data-testid="aviso-cierre-todas"
                  >
                    Todas
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs"
                    disabled={paso !== "armar"}
                    onClick={() => setElegidas([])}
                    data-testid="aviso-cierre-ninguna"
                  >
                    Ninguna
                  </Button>
                </div>
              </div>
              {agencias.length === 0 ? (
                <p className="text-sm text-slate-400 py-3 text-center" data-testid="aviso-cierre-sin-agencias">
                  Ninguna agencia tiene correo de aviso. Se cargan en Configuración → Agencias.
                </p>
              ) : (
                <div className="max-h-56 overflow-y-auto rounded-md border border-slate-200 divide-y divide-slate-100">
                  {agencias.map((a) => (
                    <label key={a.id} className="flex items-start gap-2.5 px-3 py-2 cursor-pointer hover:bg-slate-50">
                      <Checkbox
                        checked={elegidas.includes(a.id)}
                        onCheckedChange={(v) => alternar(a.id, !!v)}
                        disabled={paso !== "armar"}
                        className="mt-0.5"
                        data-testid={`aviso-cierre-agencia-${a.id}`}
                      />
                      <span className="min-w-0">
                        <span className="text-sm font-medium text-slate-800">{a.nombre}</span>
                        {!a.vende_este_tour && (
                          <span className="ml-2 text-[10px] text-slate-400">no vende este tour</span>
                        )}
                        <span className="block text-[11px] text-slate-500 break-all">{a.correos.join(", ")}</span>
                      </span>
                    </label>
                  ))}
                </div>
              )}
              {data.sin_correo?.length > 0 && (
                <details className="text-[11px] text-slate-500" data-testid="aviso-cierre-sin-correo">
                  <summary className="cursor-pointer">
                    {data.sin_correo.length} agencia(s) sin correo de aviso (se cargan en Configuración → Agencias)
                  </summary>
                  <p className="mt-1">{data.sin_correo.join(", ")}</p>
                </details>
              )}
              {correos > maximo && (
                <p className="text-xs text-rose-600" data-testid="aviso-cierre-demasiados">
                  Son {correos} correos y el máximo por aviso es {maximo}: mandalo en dos tandas.
                </p>
              )}
            </div>

            {paso === "confirmar" && (
              <p
                className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900"
                data-testid="aviso-cierre-confirmacion"
              >
                ¿Mandar el aviso a {elegidas.length} agencia(s), {correos} correo(s)? Les llega de verdad.
              </p>
            )}
          </div>
        )}

        <DialogFooter>
          {paso === "listo" ? (
            <Button onClick={() => onOpenChange(false)} data-testid="aviso-cierre-cerrar">
              Cerrar
            </Button>
          ) : paso === "confirmar" || paso === "enviando" ? (
            <>
              <Button variant="outline" onClick={() => setPaso("armar")} disabled={ocupado} data-testid="aviso-cierre-volver">
                Volver
              </Button>
              <Button onClick={mandar} disabled={ocupado} data-testid="aviso-cierre-confirmar">
                {ocupado ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> Mandando… puede tardar unos segundos
                  </>
                ) : (
                  "Sí, mandar"
                )}
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="aviso-cierre-cancelar">
                Cancelar
              </Button>
              <Button
                onClick={() => setPaso("confirmar")}
                disabled={!puedeMandar}
                className="gap-1.5"
                data-testid="aviso-cierre-mandar"
              >
                <Send className="h-4 w-4" /> Mandar el aviso ({correos} correo{correos === 1 ? "" : "s"})
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
